import type { FileUploaderFieldValue } from '@/types/formField.types';
import type { ReactNode } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { CreateReportDtoStatus } from '@api/WhisApi/generated';

export enum ReportCreationStep {
  generalInformation = 1,
  surveillanceAndEtiology = 2,
  diagnosticProcedure = 3,
  supportingDocuments = 4,
}

export enum YesNoUnknownOption {
  yes = 'yes',
  no = 'no',
  iDontKnow = 'iDontKnow',
}

/**
 * Represents a single step in the report creation process.
 */
export interface ReportCreationStepContent {
  /**
   * The title/name of the step
   */
  title: string;
  /** Form sections for this step */
  sections?: FormSection[];
}

/**
 * Field types supported by the form
 */
export type FieldType =
  | 'toggle-group'
  | 'select'
  | 'species-search'
  | 'location'
  | 'checkbox'
  | 'date'
  | 'numeric'
  | 'textarea'
  | 'autocomplete-search'
  | 'file-uploader';

export interface FieldWarningRule<
  FormValues extends FieldValues = ReportFormData,
> {
  /** Unique warning identifier */
  id: string;
  /** Fields that drive the warning visibility */
  dependsOn?: FieldPath<FormValues>[];
  /** Whether the warning should be shown */
  isVisible: (
    getValue: (name: FieldPath<FormValues>) => FormFieldValue,
  ) => boolean;
  /** Warning content */
  renderContent: () => ReactNode;
}

/**
 * Represents a form section with a title and fields
 */
export interface FormSection<FormValues extends FieldValues = ReportFormData> {
  /** Section title (optional, some sections may not have a title) */
  title?: string;
  /** Fields in this section */
  fields: FormField<FormValues>[];
}

/**
 * Conditional field visibility configuration
 */
export interface ConditionalVisibility {
  /** ID of the field this field depends on */
  dependsOn: string;
  /** Value (or list of values) that the dependency must satisfy for the field to remain visible */
  visibleWhen: string | string[] | boolean;
}

/**
 * Base interface for all form fields
 */
export interface BaseFormField<
  FormValues extends FieldValues = ReportFormData,
> {
  /** Unique field identifier */
  id: FieldPath<FormValues>;
  /** Field type */
  type: FieldType;
  /** Field label/title (optional, displayed above the field) */
  label?: string;
  /** Placeholder text (optional) */
  placeholder?: string;
  /** Whether the field is required */
  required: boolean;
  /** Conditional visibility configuration (optional) */
  conditional?: ConditionalVisibility;
  /** Warnings shown below the field */
  warnings?: FieldWarningRule<FormValues>[];
}

/**
 * Shared option shape (value + label) for selects, toggles, and autocomplete pickers.
 */
export interface FormOption {
  /** Value stored in the form state */
  value: string;
  /** Label displayed to the user */
  label: string;
}

export interface ToggleGroupField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'toggle-group';
  /** Options for the toggle group */
  options: FormOption[];
}

/**
 * Autocomplete search field (modal-based)
 */
export interface AutocompleteField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'autocomplete-search';
  /** Options shown inside the modal */
  options: FormOption[];
  /** When true, users can submit custom values not present in options. */
  allowCustomValue?: boolean;
  /** Hint or helper text shown below the search input before the user types. */
  helperText?: string;
  /**
   * Optional configuration used to swap options based on the value of another field.
   * Useful for cascading pickers (e.g. Etiological agent depends on Etiology type).
   */
  dynamicOptions?: DynamicSelectOptionConfig;
}

/**
 * Select/dropdown field
 */
export interface SelectField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'select';
  /** Options for the select dropdown */
  options: FormOption[];
  /**
   * Optional configuration used to swap options based on the value of another field.
   * Useful for cascading dropdowns (e.g. Family depends on selected Category).
   */
  dynamicOptions?: DynamicSelectOptionConfig;
}

/**
 * Species search field (navigation to taxonomy/AI/text search screens)
 */
export interface SpeciesSearchField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'species-search';
}

export interface DynamicSelectOptionConfig {
  /** Field ID that drives the option mapping */
  dependsOn: string;
  /** Map of dependency value -> select options */
  optionsByValue: Record<string, FormOption[]>;
}

/**
 * Location field (latitude + longitude + pin button)
 */
export interface LocationField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'location';
  /** Placeholder for latitude input */
  latitudePlaceholder?: string;
  /** Placeholder for longitude input */
  longitudePlaceholder?: string;
}

/**
 * Checkbox field
 */
export interface CheckboxField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'checkbox';
}

/** Date picker field */
export interface DateField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'date';
}

/**
 * Numeric input field (only positive numbers or null)
 */
export interface NumericField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'numeric';
  /** Optional automatic calculation rule (e.g. sum of other numeric fields) */
  autoCalculate?: NumericAutoCalculateRule;
}

/**
 * Automatic numeric calculation rule configuration.
 * Enables declarative formulas for numeric fields, making behaviors
 * such as "Total = Positive + Negative" part of the form model.
 */
export interface NumericAutoCalculateRule {
  /**
   * List of field IDs whose values are used for the calculation.
   * IDs must include any necessary prefixes (e.g. diagnosticProcedures[0].results.positiveCount)
   * before being consumed by a renderer.
   */
  dependsOn: string[];
  /**
   * Indicates how the dependent values should be combined.
   * `sum` currently covers the Diagnostic Procedure requirements.
   */
  mode: 'sum';
  /**
   * When true, the numeric field becomes read-only whenever the
   * calculation succeeds, keeping user input aligned with the formula.
   */
  lockWhenCalculated?: boolean;
}

/**
 * Text area field (multi-line text input)
 */
export interface TextAreaField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'textarea';
  /** Maximum number of characters allowed */
  maxLength?: number;
}

/**
 * File uploader field for supporting documents and attachments
 */
export interface FileUploaderField<
  FormValues extends FieldValues = ReportFormData,
> extends BaseFormField<FormValues> {
  type: 'file-uploader';
  /** Supported file extensions (lowercase) */
  acceptedFormats: string[];
  /** Maximum allowed file size in bytes */
  maxFileSizeInBytes: number;
  /** Whether multiple files can be uploaded */
  multiple: boolean;
  /** Helper text shown inside the uploader */
  helperText?: string;
  /** Prompt text used inside the upload area */
  uploadPrompt?: string;
}

/**
 * Union type of all form fields
 */
export type FormField<FormValues extends FieldValues = ReportFormData> =
  | ToggleGroupField<FormValues>
  | SelectField<FormValues>
  | SpeciesSearchField<FormValues>
  | LocationField<FormValues>
  | CheckboxField<FormValues>
  | DateField<FormValues>
  | NumericField<FormValues>
  | TextAreaField<FormValues>
  | AutocompleteField<FormValues>
  | FileUploaderField<FormValues>;

/**
 * Primitive values stored in the form state.
 */
export type FormFieldValue =
  | string
  | boolean
  | number
  | Date
  | FileUploaderFieldValue
  | null
  | undefined;

/**
 * Recursive representation of any value stored in the form, including arrays
 * and nested objects required by repeatable sections.
 */
export type FormDataValue =
  | FormFieldValue
  | FormDataValue[]
  | { [key: string]: FormDataValue };

export interface FormLocationValue {
  latitude?: string;
  longitude?: string;
}

export interface DiagnosticProcedureFormValue {
  sampling?: {
    sampleType?: string;
  };
  tests?: {
    category?: string;
    subcategory?: string;
    family?: string;
    extraInfo?: string;
  };
  results?: {
    positiveCount?: number;
    negativeCount?: number;
    totalCount?: number;
  };
}

export interface ReportFormData extends FieldValues {
  animalType?: string;
  species?: string;
  wildtype?: string;
  location?: FormLocationValue;
  inaccurateLocation?: boolean;
  dateOfDetection?: Date | string;
  surveillanceType?: string;
  description?: string;
  numberOfDeaths?: number;
  numberOfSick?: number;
  populationSize?: number;
  humanAffected?: YesNoUnknownOption;
  domesticAnimalAffected?: YesNoUnknownOption;
  knowTheCause?: YesNoUnknownOption;
  etiologyType?: string;
  etiologyAgent?: string;
  diagnosticProcedure?: 'yes' | 'no';
  diagnosticProcedures?: DiagnosticProcedureFormValue[];
  supportingDocuments?: FileUploaderFieldValue;
}

export type ArrayFieldKeys<FormValues extends FieldValues> = {
  [K in keyof FormValues]: FormValues[K] extends unknown[] ? K : never;
}[keyof FormValues];

/**
 * Configuration for a repeatable section block.
 * Each block defines the structure of one "instance" (e.g. Diagnostic Procedure #1)
 * and the renderer duplicates it dynamically by prefixing field IDs with the field-array path.
 */
export interface RepeatableSectionConfig<
  FormValues extends FieldValues = ReportFormData,
  FieldName extends ArrayFieldKeys<FormValues> = ArrayFieldKeys<FormValues>,
> {
  /** Form path used as the root array name (e.g. diagnosticProcedures) */
  id: FieldName;
  /** Base title displayed before "#<index>" */
  title: string;
  /** Sections that make up a single repeatable block */
  sections: FormSection<FormValues>[];
  /** Optional minimum number of blocks (defaults to 1) */
  minInstances?: number;
  /** Optional maximum number of blocks */
  maxInstances?: number;
  /** Label displayed on the top of the button used to create a new block */
  addButtonSectionTitle: string;
  /** Label displayed on the button used to create a new block */
  addButtonLabel: string;
}

/**
 * Form layout describing the structure of a step.
 * Steps may combine static sections and repeatable section configs.
 */
export interface FormLayout<FormValues extends FieldValues = ReportFormData> {
  sections?: FormSection<FormValues>[];
  repeatableSections?: RepeatableSectionConfig<FormValues>[];
}

export type ReportSubmissionMode = CreateReportDtoStatus &
  ('draft' | 'published'); // Limit report submission status to published and draft
